import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useParams, useLocation, useNavigationType } from 'react-router-dom';
import { artistsApi } from '../services/api';
import { CITY_SLUG } from '../config/geo';

// Manual scroll restoration: keep position on same-path filter changes, restore
// exact scroll on Back/Forward (POP), scroll to top on new-path navigation.
export function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    try { window.history.scrollRestoration = 'manual'; } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    const key = 'as_scroll:' + location.key;
    if (navType === 'POP') {
      const saved = sessionStorage.getItem(key);
      const y = saved != null ? parseInt(saved, 10) : 0;
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, y)));
    } else if (location.pathname !== prevPath.current) {
      window.scrollTo(0, 0);
    }
    prevPath.current = location.pathname;

    // R2 — honour #hash targets (e.g. /delhi-ncr#occasions) once the section renders.
    let tries = 0;
    const hashTimer = location.hash ? setInterval(() => {
      const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (el) { el.scrollIntoView({ block: 'start' }); clearInterval(hashTimer); } else if (++tries > 30) clearInterval(hashTimer);
    }, 100) : null;

    const onScroll = () => {
      try { sessionStorage.setItem('as_scroll:' + location.key, String(window.scrollY)); } catch (e) { /* ignore */ }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { if (hashTimer) clearInterval(hashTimer); onScroll(); window.removeEventListener('scroll', onScroll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return null;
}

// Trailing slash -> no slash (keeps query + hash), replace navigation.
export function TrailingSlashRedirect() {
  const location = useLocation();
  const { pathname, search, hash } = location;
  if (pathname.length > 1 && pathname.endsWith('/')) {
    const clean = pathname.replace(/\/+$/, '') || '/';
    return <Navigate to={`${clean}${search}${hash}`} replace />;
  }
  return null;
}

// "/" -> remembered city hub (only delhi-ncr for now).
export function RootRedirect() {
  let city = CITY_SLUG;
  try {
    const m = document.cookie.match(/(?:^|; )as_loc=([^;]+)/);
    if (m) {
      const parsed = JSON.parse(decodeURIComponent(m[1]));
      if (parsed && parsed.city) city = parsed.city;
    }
  } catch (e) { /* ignore */ }
  return <Navigate to={`/${city}`} replace />;
}

// "/artists" -> "/delhi-ncr/artists" keeping the query string.
export function LegacyListingRedirect() {
  const location = useLocation();
  return <Navigate to={`/${CITY_SLUG}/artists${location.search}`} replace />;
}

// "/artists/:id" -> "/artist/:slug"
export function LegacyArtistRedirect() {
  const { id } = useParams();
  const [slug, setSlug] = useState(undefined); // undefined=loading

  useEffect(() => {
    let active = true;
    artistsApi
      .resolve({ id })
      .then((r) => { if (active) setSlug(r.data.slug || null); })
      .catch(() => { if (active) setSlug(null); });
    return () => { active = false; };
  }, [id]);

  if (slug === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-[#FF1D25] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (slug) return <Navigate to={`/artist/${slug}`} replace />;
  return <Navigate to={`/${CITY_SLUG}/artists`} replace />;
}
